import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../../components/feature/Layout';
import { useSEO, generateWebPageSchema } from '../../../utils/seo';

const SocialSummaryPage: React.FC = () => {
  // FAQ設定
  const faqs = [
    {
      question: '社会が苦手な子は、どこから始めればいいですか？',
      answer: 'まずは「地図」と「年表」を使って、全体像を把握することから始めましょう。地理は地図、歴史は年表で流れを整理すると、個別の知識が頭に入りやすくなります。',
    },
    {
      question: '歴史の流れが覚えられない子への声かけは？',
      answer: '「何年に何があった」ではなく、「なぜそうなったか」を一緒に考えることが大切です。「この出来事、前の時代と何が違う？」と問いかけることで、歴史の流れが理解しやすくなります。',
    },
    {
      question: '地理の地名や用語が覚えられない場合は？',
      answer: '地図を見ながら、「ここはどんな場所？」「何が有名？」と具体的にイメージすることが効果的です。写真や動画を見せると、より記憶に残りやすくなります。',
    },
    {
      question: '中学社会の無料教材サイトは、どれを選べばいいですか？',
      answer: '基礎固めなら「ちびむすドリル」、動画で学びたいなら「平均ドリル」がおすすめです。高校受験対策には「無料で使える中学学習プリント」が便利です。',
    },
    {
      question: '居場所や家庭で社会を教える際の注意点は？',
      answer: '「暗記」だけでなく、「なぜそうなったか」を一緒に考えることが大切です。ニュースや身近な出来事と結びつけると、社会への興味が高まります。',
    },
  ];

  // SEO設定
  useSEO({
    title: '中学社会まとめ｜地理・歴史・公民の学習ガイド｜キョウコレ',
    description: '中学社会の学習内容を学年別・単元別に整理。地理・歴史・公民の流れと用語整理、おすすめの無料教材サイトをご紹介します。',
    keywords: '中学社会,社会まとめ,地理,歴史,公民,学習ガイド,無料教材',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/features/social-summary',
    jsonLd: generateWebPageSchema(
      '中学社会まとめ｜地理・歴史・公民の学習ガイド｜キョウコレ',
      '中学社会の学習内容を学年別・単元別に整理。地理・歴史・公民の流れと用語整理、おすすめの無料教材サイトをご紹介します。',
      '/features/social-summary'
    ),
  });

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 py-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* ヘッダー */}
          <div className="text-center mb-12">
            <div className="inline-block w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center text-4xl mb-4">
              🌏
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              中学社会の無料教材まとめ
            </h1>
            <p className="text-lg text-gray-600">
              学年別・分野別に整理した学習ガイドと、おすすめの無料教材サイトをご紹介
            </p>
          </div>

          {/* このページでわかること */}
          <div className="bg-white rounded-xl shadow-sm p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-4 border-yellow-500 pb-2">
              このページでわかること
            </h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-yellow-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>中学社会を「地理」「歴史」「公民」の3分野に分けて整理する方法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-yellow-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>学年別・分野別のつまずきポイントと攻略法</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-yellow-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>無料で使える社会教材サイトの特徴と使い分け</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-yellow-600 mr-2 mt-1 flex-shrink-0"></i>
                <span>居場所や家庭で社会を教える際のコツ</span>
              </li>
            </ul>
          </div>

          {/* 社会はこう整理すると進めやすい */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-yellow-500 pb-2">
              社会はこう整理すると進めやすい
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                中学社会は「地理」「歴史」「公民」の3つの分野で構成されています。この3つは独立しているようで、実は深くつながっています。例えば、歴史を理解するには地理の知識が必要ですし、公民を理解するには歴史の流れが役立ちます。
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>地理・歴史・公民と「流れ」「地図」「用語」整理：</strong>社会が苦手な子の多くは、「用語の暗記」だけで終わっています。しかし、社会は「流れ」「地図」「用語」の3つを組み合わせて理解することが大切です。地理は地図で位置を確認し、歴史は年表で流れを整理し、公民は現代社会とのつながりを意識することで、知識が定着します。
              </p>
              <div className="bg-yellow-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">学習の進め方（推奨順序）</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</span>
                    <div>
                      <strong>地図・年表で全体像を把握</strong>：地理は地図、歴史は年表で流れを整理
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</span>
                    <div>
                      <strong>用語の暗記</strong>：重要な地名・人物・出来事を覚える
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</span>
                    <div>
                      <strong>流れの理解</strong>：「なぜそうなったか」を考える
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 font-bold">4</span>
                    <div>
                      <strong>応用問題</strong>：複数の分野を組み合わせた問題に挑戦
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* 学年別・分野別の整理 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-yellow-500 pb-2">
              学年別・分野別の整理
            </h2>

            {/* 中1社会（地理） */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-3">1</span>
                中1社会のポイント（地理分野）
              </h3>
              <p className="text-gray-700 mb-4">
                中1では「世界地理」を中心に学びます。地図の見方、世界の国々の特徴、気候や産業などを理解します。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">世界の姿</h4>
                  <p className="text-sm text-gray-600">
                    地球儀と世界地図、緯度・経度、六大陸と三大洋を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">世界各地の人々の生活と環境</h4>
                  <p className="text-sm text-gray-600">
                    気候帯、宗教、衣食住の違いを理解する。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">世界の諸地域（アジア・ヨーロッパ・アフリカ等）</h4>
                  <p className="text-sm text-gray-600">
                    各地域の自然環境、産業、文化を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">日本の姿</h4>
                  <p className="text-sm text-gray-600">
                    日本の位置、領域、都道府県を理解する。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中2社会（歴史・地理） */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-3">2</span>
                中2社会のポイント（歴史分野・地理分野）
              </h3>
              <p className="text-gray-700 mb-4">
                中2では「日本の歴史」と「日本地理」を学びます。歴史は古代から近代まで、地理は日本の各地方の特徴を理解します。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">古代までの日本（歴史）</h4>
                  <p className="text-sm text-gray-600">
                    縄文・弥生時代、古墳時代、飛鳥・奈良時代を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">中世の日本（歴史）</h4>
                  <p className="text-sm text-gray-600">
                    平安時代、鎌倉時代、室町時代を理解する。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">近世の日本（歴史）</h4>
                  <p className="text-sm text-gray-600">
                    安土桃山時代、江戸時代を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">日本の地域的特色（地理）</h4>
                  <p className="text-sm text-gray-600">
                    日本の自然環境、人口、資源・エネルギー、産業を理解する。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">日本の諸地域（地理）</h4>
                  <p className="text-sm text-gray-600">
                    九州・中国四国・近畿・中部・関東・東北・北海道の特徴を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>

            {/* 中3社会（歴史・公民） */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-3">3</span>
                中3社会のポイント（歴史分野・公民分野）
              </h3>
              <p className="text-gray-700 mb-4">
                中3では「近現代の歴史」と「公民」を学びます。高校受験対策も本格化するため、計画的な学習が必要です。
              </p>
              <div className="space-y-3">
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">近代の日本と世界（歴史）</h4>
                  <p className="text-sm text-gray-600">
                    明治維新、日清・日露戦争、第一次世界大戦を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">現代の日本と世界（歴史）</h4>
                  <p className="text-sm text-gray-600">
                    第二次世界大戦、戦後の日本、冷戦を理解する。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">現代社会と私たち（公民）</h4>
                  <p className="text-sm text-gray-600">
                    現代社会の特色、文化、グローバル化を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">人間の尊重と日本国憲法（公民）</h4>
                  <p className="text-sm text-gray-600">
                    基本的人権、平和主義、国民主権を理解する。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">民主政治と政治参加（公民）</h4>
                  <p className="text-sm text-gray-600">
                    国会・内閣・裁判所、地方自治を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">私たちの暮らしと経済（公民）</h4>
                  <p className="text-sm text-gray-600">
                    市場経済、金融、財政、社会保障を理解する。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-bold text-gray-900 mb-1">国際社会と私たち（公民）</h4>
                  <p className="text-sm text-gray-600">
                    国際連合、国際問題、持続可能な社会を学ぶ。<Link to="/materials" className="text-yellow-600 hover:underline">単元ガイドで詳しく見る</Link>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* おすすめ無料教材サイト */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-yellow-500 pb-2">
              おすすめ無料教材サイト一覧
            </h2>
            <p className="text-gray-700 mb-6">
              中学社会の学習に役立つ無料教材サイトを厳選しました。それぞれの特徴を理解して、目的に合わせて使い分けましょう。
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-yellow-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://happylilac.net/highschool.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-600 hover:text-yellow-700 hover:underline cursor-pointer"
                  >
                    ちびむすドリル【中学生】
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>地理・歴史・公民を幅広くカバー。印刷向け
                </p>
                <p className="text-sm text-gray-700">
                  学年別・単元別に整理されたプリントが豊富です。基礎固めに特化しており、社会が苦手な子でも取り組みやすい内容です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-yellow-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://heikin.space/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-600 hover:text-yellow-700 hover:underline cursor-pointer"
                  >
                    平均ドリル
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>無料授業動画とプリントで効率学習。自習向け
                </p>
                <p className="text-sm text-gray-700">
                  授業動画を見てから問題を解く形式で、自宅学習に最適です。歴史の流れや地理の特徴が動画で分かりやすく解説されています。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-yellow-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://desafiante.net/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-600 hover:text-yellow-700 hover:underline cursor-pointer"
                  >
                    ぷりんと保管庫
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>社会科の基礎固めに最適。自習向け
                </p>
                <p className="text-sm text-gray-700">
                  基礎固めに特化したプリントが豊富で、自宅学習に最適です。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-yellow-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <a
                    href="https://www.okadori.net/category/tyuugaku/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-600 hover:text-yellow-700 hover:underline cursor-pointer"
                  >
                    おかわりドリル 中学生
                    <i className="ri-external-link-line ml-2"></i>
                  </a>
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>何が強いサイトか：</strong>社会科の重要ポイントを押さえた問題集
                </p>
                <p className="text-sm text-gray-700">
                  重要ポイントに絞った問題集で、効率的に学習できます。
                </p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-yellow-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  <Link
                    to="/sites"
                    className="text-yellow-600 hover:text-yellow-700 hover:underline cursor-pointer"
                  >
                    その他の社会教材サイトを見る
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </h3>
                <p className="text-sm text-gray-700">
                  キョウコレでは、さらに多くの無料教材サイトを紹介しています。教科や単元で絞り込んで検索できます。
                </p>
              </div>
            </div>
          </section>

          {/* 居場所・家庭での使い方 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-yellow-500 pb-2">
              居場所・家庭での使い方
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                社会は「暗記」だけでなく、「なぜそうなったか」を一緒に考えることが大切です。居場所や家庭で社会を教える際のコツをまとめました。
              </p>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">1. 「地図・年表を一緒に見る」</h3>
                  <p className="text-gray-700 leading-relaxed">
                    地理は地図、歴史は年表を一緒に見ながら学習すると、全体像が把握しやすくなります。「この国、どこにある？」「この出来事、いつ頃？」と問いかけることで、子どもも考えやすくなります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">2. 「ニュースや身近な出来事と結びつける」</h3>
                  <p className="text-gray-700 leading-relaxed">
                    公民は特に、ニュースや身近な出来事と結びつけると理解が深まります。「このニュース、教科書のどこに関係ある？」と問いかけることで、社会への興味が高まります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">3. 「なぜそうなったか」を一緒に考える</h3>
                  <p className="text-gray-700 leading-relaxed">
                    歴史は特に、「なぜそうなったか」を一緒に考えることが大切です。「この戦争、なぜ起きたと思う？」と問いかけることで、歴史の流れが理解しやすくなります。
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">4. 活用レシピを参考にする</h3>
                  <p className="text-gray-700 leading-relaxed">
                    キョウコレの<Link to="/recipes" className="text-yellow-600 hover:underline">活用レシピ</Link>では、他の居場所や家庭での実践例が共有されています。「こんな声かけが効果的だった」「この教材の使い方が良かった」といったアイデアを参考にしてみましょう。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-yellow-500 pb-2">
              よくある質問
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-sm p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-start">
                    <span className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                      Q
                    </span>
                    {faq.question}
                  </h3>
                  <div className="ml-11">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ボラタス導線 */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b-4 border-yellow-500 pb-2">
              オンライン学習支援を探すなら（ボラタス）
            </h2>
            <div className="bg-white rounded-xl shadow-sm p-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                教材だけでなく、オンラインで学習をサポートしてくれるボランティアを探すこともできます。居場所や家庭での学習支援に、ぜひご活用ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://boratasu.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスでボランティアを探す
                  <i className="ri-external-link-line ml-2"></i>
                </a>
                <a
                  href="https://zion-craft.work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-3 bg-slate-600 text-white font-medium rounded-lg hover:bg-slate-700 transition-colors duration-200 cursor-pointer whitespace-nowrap text-center"
                >
                  ボラタスについて詳しく知る
                  <i className="ri-external-link-line ml-2"></i>
                </a>
              </div>
            </div>
          </section>

          {/* まとめ */}
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">
              中学社会は、流れ・地図・用語を整理すれば理解しやすくなります
            </h2>
            <p className="text-lg leading-relaxed mb-6">
              このページで紹介した無料教材サイトや学習のコツを活用して、<br />
              子どもたちの「分かった！」を増やしていきましょう。
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/materials?subject=社会"
                className="inline-block px-6 py-3 bg-white text-yellow-700 font-medium rounded-lg hover:bg-yellow-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                社会の単元ガイド一覧を見る
              </Link>
              <Link
                to="/sites"
                className="inline-block px-6 py-3 bg-white text-yellow-700 font-medium rounded-lg hover:bg-yellow-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                教材サイト一覧を見る
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SocialSummaryPage;
